import {HiristBanner} from '@jobseeker/lotus/components';
import {Box, IconButton, Typography, useTheme} from '@mui/material';
import type {JobDetail} from '@jobseeker/common-core/stateModels/job/jobdetail.models';
import Pill from './Pill';
import {Image} from '@jobseeker/lotus/components/Image';
import {
  DefaultJobLogo,
  HiristInfoIcon,
  LeftArrow,
  StarIconHirist,
  WhiteDivider,
} from '@jobseeker/lotus/assets/icons';
import {getRandomColor} from '@jobseeker/lotus/theme';
import type {Showcase} from '@jobseeker/common-core/types/job';
import {Button, Tooltip} from '@jobseeker/lotus/elements';
import {cleanUrl} from '@jobseeker/lotus/utils';
import {FaBookmark} from 'react-icons/fa';
import {useEffect, useState} from 'react';
import {VersionVariants} from '@jobseeker/lotus/enums';
import Link from 'next/link';
import {openUrl} from '@jobseeker/common-core/utils/common';
import {getUbaPageName, TrackEventUBA} from '@jobseeker/common-core/utils';
import {dateToDuration} from '@jobseeker/lotus/utils/date';
import moment from 'moment';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';

type JobDetailHeaderProps = {
  title?: string;
  company?: string;
  centralizedCompanyId?: number;
  locations?: {id: number; name: string}[];
  experience?: string;
  tags?: string[];
  postedOn?: string;
  confidential?: number;
  showcase: Showcase;
  premium: number;
  active?: boolean;
  companyStatus?: number;
  careerCompanyName?: string;
  companyLogoFilePath?: string;
  isMobile?: boolean;
  jobId?: string | number;
  showOnlyHeader?: boolean;
  jobData?: JobDetail;
  brandedJob?: boolean;
  createdTime?: number;
  isConsultant?: boolean;
};

const DefaultLogo = ({jobId = 0, isDeviceLarge = true}) => {
  const theme = useTheme();
  const randomColor = getRandomColor(jobId);
  return (
    <Box
      className="joblist__logo"
      sx={{
        background: randomColor,
        p: theme.spacing(1.5),
        lineHeight: 0,
        borderRadius: '10px',
        alignSelf: 'start',
        width: 'fit-content',
        height: 'fit-content',
      }}
    >
      <Image
        src={DefaultJobLogo}
        alt={'Logo'}
        height={isDeviceLarge ? 76 : 25}
        width={isDeviceLarge ? 76 : 25}
        objectFit="cover"
        quality={50}
      />
    </Box>
  );
};

const Dot = () => {
  return (
    <Box
      sx={{
        width: 4,
        height: 4,
        backgroundColor: '#cecece',
        borderRadius: '50%',
        display: 'inline-block',
        mr: 1.5,
        ml: 1.5,
        mt: '-2px',
        verticalAlign: 'middle',
      }}
    />
  );
};

const Header = (props: Partial<JobDetailHeaderProps>) => {
  const {
    jobId = 0,
    title = '',
    active = true,
    company = '',
    locations = [],
    experience = '',
    tags = [],
    postedOn = '',
    confidential = 0,
    premium = 0,
    showcase = {
      showcaseName: '',
      showcaseCompanyId: 0,
      showcaseDomain: '',
      showcaseImages: [],
      showcaseUrl: '',
      showcaseVersion: '',
    },
    companyLogoFilePath = '',
    isMobile = false,
    showOnlyHeader = false,
    createdTime = 0,
  } = props;

  const getDeviceOs = (userAgent: string): string => {
    const isMobile =
      /(iPad|iPhone|Android|webOS|iPod|BlackBerry|IEMobile|Opera Mini|Mobile)/i.test(
        userAgent
      ) || false;

    if (isMobile) {
      if (/android/i.test(userAgent)) {
        return 'android';
      }

      if (/iphone|ipad|ipod|iPhone|ios/.test(userAgent)) {
        return 'ios';
      }
    }

    return 'web';
  };
  const useUserAgent = () => {
    const [userAgent, setUserAgent] = useState<string>('');

    useEffect(() => {
      if (typeof navigator !== 'undefined') {
        setUserAgent(navigator.userAgent || navigator.vendor || '');
      }
    }, []);

    return userAgent;
  };
  const userAgent = useUserAgent();

  const isDeviceLarge = !isMobile;
  const isLargeDevice = useIsLargeDevice(1200);

  const [tagsSize, setTagsSize] = useState(2);
  const slug = props?.jobData?.companyData?.ambitionBoxInfo?.seo?.slug;

  const detailsMissing = title === '';

  const ambitionBoxInfo = props?.jobData?.companyData?.ambitionBoxInfo;
  const showcasePage = props?.jobData?.showcase;
  const createdTimeMs = props?.jobData?.createdTimeMs;

  const rating = ambitionBoxInfo?.aggregateRatingOneDecimal;
  const reviewsCount = ambitionBoxInfo?.reviewsCount;
  const isRatingValid =
    typeof rating === 'number' && rating >= (props?.jobData?.minRatingAb || 0);
  const hasReviews = typeof reviewsCount === 'number' && reviewsCount > 0;
  const isNotShowcase = !showcasePage;
  const isActiveJob =
    typeof createdTimeMs === 'number' &&
    Date.now() - createdTimeMs <= 180 * 24 * 60 * 60 * 1000;

  const showRating =
    ambitionBoxInfo &&
    isRatingValid &&
    hasReviews &&
    isNotShowcase &&
    isActiveJob;

  // useEffect(() => {
  //   const fetchSlug = async () => {
  //     try {
  //       const response = await jobDetailService.getCompanySlugById(
  //         props?.jobData?.recruiter?.centralizedCompanyId || -1
  //       );
  //       setSlug(response?.data?.slug); // Update the state with the fetched slug
  //     } catch (error) {
  //       console.error('Error fetching slug:', error);
  //     }
  //   };

  //   if (props?.jobData?.recruiter?.centralizedCompanyId) {
  //     fetchSlug();
  //   }
  // }, [props?.jobData]); // Runs whenever `jobData` changes

  if (detailsMissing) {
    return (
      <Box mt={props.brandedJob ? 4.875 : 2.875}>
        <HiristBanner
          sx={
            props.brandedJob
              ? {
                  py: 3,
                  backgroundImage: 'none !important',
                  bgcolor: '#1C2D41 !important',
                  minHeight: 'auto !important',
                }
              : {}
          }
        >
          <Box display="flex" flexDirection="column">
            <Typography fontSize={38} fontWeight={700} color="white">
              Oops!
            </Typography>
            <Typography fontSize={26} fontWeight={300} color="white" mb={1}>
              This job has been permanently closed or removed by the
              administrator
            </Typography>
          </Box>
        </HiristBanner>
        {!showOnlyHeader && (
          <Box textAlign="center" mt={5}>
            <Typography fontSize={24} fontWeight={600}>
              Sorry, No result found :(
              <br />
              To get results, Please change your search criteria or filters like
              posting range from the above.
            </Typography>
            <Image
              src="https://job-static.hirist.com/V3/static/media/no-result-illustration.d2c11a15.svg"
              width={457}
              height={400}
              alt="job-not-found"
            />
          </Box>
        )}
      </Box>
    );
  }

  const {
    showcaseDomain = '',
    showcaseImages = [],
    showcaseUrl = '',
    showcaseVersion = '',
    showcaseName = '',
    showcaseCompanyId = 0,
  } = showcase;

  const logoUrl = confidential
    ? ''
    : companyLogoFilePath?.length > 0
    ? companyLogoFilePath
    : '';

  const hasShowcase =
    showcase &&
    (showcaseVersion === 'v2' ||
      (showcaseVersion === 'v1' && showcaseDomain !== ''));

  const showcaseUrlWithQueryParams =
    showcaseUrl +
    (showcaseUrl.includes('?') ? '&src=hirist&ref=jdp' : '?src=hirist&ref=jdp');

  const companyPageUrl = confidential
    ? '#'
    : slug?.length > 0
    ? `${process.env.NEXT_PUBLIC_LOAD_CONFIG}/${cleanUrl(slug)}-careers`
    : '#';

  const companyPageRedirect = async () => {
    setTimeout(() => {
      if (slug) {
        TrackEventUBA('companyDetailsClick', 'click', {
          sessionUsername: sessionStorage.getItem('userId') || '',
        });
        openUrl(
          `/company/${slug}-careers?ref=jdp&utm_source=${
            process.env.NEXT_PUBLIC_APP_NAME
          }&utm_medium=${getDeviceOs(userAgent)}&utm_campaign=${
            process.env.NEXT_PUBLIC_APP_NAME
          }_jd_ratings`
        );
      }
    }, 0);
  };

  return (
    <Box
      mt={
        isDeviceLarge
          ? props?.brandedJob
            ? 4.875
            : 2.875
          : props?.brandedJob
          ? 0
          : 4
      }
    >
      <HiristBanner
        sx={
          props.brandedJob
            ? {
                py: 3,
                backgroundImage: 'none !important',
                bgcolor: '#1C2D41 !important',
                minHeight: 'auto !important',
              }
            : {}
        }
      >
        {props.isConsultant ? (
          <></>
        ) : logoUrl.length > 0 ? (
          <IconButton
            onClick={() => {
              if (companyPageUrl !== '#') {
                setTimeout(() => openUrl(companyPageUrl, '_blank'), 0);
              }
            }}
            sx={{
              p: 0,
              pt: 0.5,
              ...(companyPageUrl === '#' && {':hover': {cursor: 'default'}}),
            }}
          >
            <Image
              src={logoUrl}
              alt="company-icon"
              height={isDeviceLarge ? 100 : 50}
              width={isDeviceLarge ? 100 : 50}
              style={{
                alignSelf: 'start',
                borderRadius: props.brandedJob ? '12px' : '8px',
                backgroundColor: 'white',
                objectFit: 'contain',
                ...(!isDeviceLarge && {position: 'relative', top: '-4px'}),
              }}
            />
          </IconButton>
        ) : (
          <DefaultLogo isDeviceLarge={isDeviceLarge} />
        )}
        <Box
          display="flex"
          flexDirection="column"
          ml={
            props.isConsultant
              ? 0
              : isDeviceLarge
              ? props.brandedJob
                ? 2
                : 4
              : 2
          }
        >
          <Typography
            component={'h1'}
            fontSize={isDeviceLarge ? (props.brandedJob ? 20 : 24) : 16}
            fontWeight={700}
            color="white"
            mb={props.brandedJob && !isDeviceLarge ? 0.5 : 1}
          >
            {title}
            {premium > 0 && isDeviceLarge && !props.brandedJob && (
              <Tooltip
                variant={VersionVariants.VERSION_HIRIST}
                arrow
                id="premium_job"
                className="set-tooltip"
                title={
                  <Box
                    sx={{
                      bgcolor: '#fbe3e6',
                      color: 'red',
                    }}
                  >
                    premium job
                  </Box>
                }
                sx={{
                  '& .MuiTooltip-tooltip, & .MuiTooltip-arrow:before': {
                    bgcolor: '#fbe3e6',
                  },
                }}
              >
                <Box display="inline" p={0}>
                  <Button
                    shape="circular"
                    icon={<FaBookmark />}
                    size="small"
                    color="secondary"
                    sx={{
                      color: '#b4b9d8',
                      border: 'none',
                      bgcolor: 'white !important',
                      height: props.brandedJob ? 24 : 32,
                      width: props.brandedJob ? 24 : 32,
                      px: props.brandedJob ? 1 : 1.45,
                      py: props.brandedJob ? 1 : 1.5,
                      cursor: 'default',
                      mt: -0.5,
                    }}
                    variant={'outlined'}
                    disableRipple
                    aria-label="premium-job"
                  />
                </Box>
              </Tooltip>
            )}
            {!active && (
              <Tooltip
                arrow
                title={
                  <Typography
                    variant="subtitle3"
                    lineHeight={2.25}
                    color="secondary.main"
                  >
                    This job opening was posted a long time ago. It may no
                    longer be active, and it hasn&apos;t been removed by the
                    recruiter. Please use your discretion before applying.
                  </Typography>
                }
              >
                <Image
                  src={HiristInfoIcon}
                  alt={'caution'}
                  data-name="old-job"
                  width={20}
                  height={20}
                  objectFit="cover"
                  quality={50}
                  style={{
                    position: 'relative',
                    top: 4,
                  }}
                />
              </Tooltip>
            )}
          </Typography>
          <Box color="white">
            <Typography
              component="span"
              fontSize={isDeviceLarge ? 16 : 14}
              data-testid="company-name"
            >
              {companyPageUrl === '#' ? (
                <span style={{color: 'white'}}>
                  {confidential ? 'Verified Recruiter' : company}
                </span>
              ) : (
                <Link
                  href={companyPageUrl}
                  onClick={e => {
                    e.preventDefault();
                    if (companyPageUrl !== '#') {
                      setTimeout(() => openUrl(companyPageUrl, '_blank'), 0);
                    }
                  }}
                  style={{textDecoration: 'none', color: 'white'}}
                  target="_blank"
                >
                  {confidential ? 'Verified Recruiter' : company}
                </Link>
              )}
            </Typography>
            <Dot />
            <Tooltip
              variant={VersionVariants.VERSION_HIRIST}
              arrow
              id="display_location"
              className="set-tooltip"
              title={
                <Box
                  component="ul"
                  // data-testid="company-location"
                  sx={{
                    listStyleType: 'square',
                    paddingLeft: 2,
                    bgcolor: '#fbe3e6',
                    color: 'red',
                  }}
                >
                  {locations?.map((info, index: number) => (
                    <Box
                      component="li"
                      fontSize={16}
                      key={index}
                      fontWeight="normal"
                      padding="2px 1px"
                    >
                      {info.name}
                    </Box>
                  ))}
                </Box>
              }
              sx={{
                '& .MuiTooltip-tooltip, & .MuiTooltip-arrow:before': {
                  bgcolor: '#fbe3e6',
                },
              }}
            >
              {locations.length === 1 &&
              locations[0].name != 'Any Location' &&
              locations[0].name != 'Anywhere in India/Multiple Locations' ? (
                <a
                  target="blank"
                  href={`/it-jobs-in-${
                    locations?.[0]?.name === 'Gurgaon/Gurugram'
                      ? 'gurgaon-gurugram'
                      : locations?.[0]?.name
                          ?.toLowerCase()
                          ?.replace(/[\/ ]/g, '-')
                  }`}
                  onClick={e => {
                    e.preventDefault();
                    const locSegment =
                      locations?.[0]?.name === 'Gurgaon/Gurugram'
                        ? 'gurgaon-gurugram'
                        : locations?.[0]?.name
                            ?.toLowerCase()
                            ?.replace(/[\/ ]/g, '-');
                    const url = `/it-jobs-in-${locSegment}`;
                    setTimeout(() => openUrl(url, '_blank'), 0);
                  }}
                  style={{textDecoration: 'none', color: 'white'}}
                >
                  {' '}
                  <Typography
                    component="span"
                    fontSize={isDeviceLarge ? 16 : 14}
                    data-testid="company-location"
                    ml={props.brandedJob ? -0.5 : 0}
                  >
                    {locations.length === 1
                      ? locations[0].name
                      : 'Multiple Locations'}
                  </Typography>
                </a>
              ) : (
                <Typography component="span" fontSize={isDeviceLarge ? 16 : 14}>
                  {locations.length === 1
                    ? locations[0].name
                    : 'Multiple Locations'}
                </Typography>
              )}
            </Tooltip>
            <Dot />
            <Typography
              component="span"
              fontSize={isDeviceLarge ? 16 : 14}
              data-testid="experience"
            >
              {experience}
            </Typography>
            {showRating && !props.jobData?.confidential && (
              <>
                <Dot />
                <Typography component="span" fontSize={isDeviceLarge ? 16 : 14}>
                  <Box
                    display="inline-flex"
                    alignItems="center"
                    sx={{cursor: 'pointer'}}
                  >
                    <Tooltip
                      variant={VersionVariants.VERSION_HIRIST}
                      arrow
                      id="display_rating"
                      className="set-tooltip"
                      title={
                        <Typography
                          sx={{
                            listStyleType: 'square',
                            bgcolor: '#fbe3e6',
                            color: 'red',
                          }}
                        >
                          Powered By AmbitionBox
                        </Typography>
                      }
                      sx={{
                        '& .MuiTooltip-tooltip, & .MuiTooltip-arrow:before': {
                          bgcolor: '#fbe3e6',
                        },
                      }}
                    >
                      <Box display="inline-flex" alignItems="center">
                        <Box display={'flex'} marginBottom={'2px'}>
                          <Image
                            src={StarIconHirist}
                            height={12}
                            width={12}
                            alt="star-icon"
                          />
                        </Box>
                        <Typography
                          component="span"
                          fontSize={isDeviceLarge ? 16 : 14}
                          sx={{ml: 0.5}}
                          onClick={companyPageRedirect}
                        >
                          {rating}
                        </Typography>
                        <Box
                          component={Image}
                          src={WhiteDivider}
                          alt="white-divider"
                          sx={{
                            width: 10,
                            height: 12,
                            objectFit: 'contain',
                            mb: 0.2,
                            display: 'inline-block',
                            verticalAlign: 'middle',
                          }}
                        />
                        <Typography
                          component="span"
                          fontSize={isDeviceLarge ? 16 : 14}
                          onClick={companyPageRedirect}
                        >
                          {`${reviewsCount?.toLocaleString()}+ Reviews`}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </Box>
                </Typography>
              </>
            )}
          </Box>
          <Box
            mt={props.brandedJob && !isDeviceLarge ? 1 : 1.25}
            mb={props.brandedJob ? (!isDeviceLarge ? 1 : 0) : 2}
            maxWidth={showcaseImages.length > 0 ? 836 : '100%'}
            data-testid="job-header-main"
          >
            {premium > 0 && !isDeviceLarge && (
              <Button
                shape={'circular'}
                icon={<FaBookmark />}
                size="small"
                color="secondary"
                sx={{
                  color: 'white',
                  bgcolor: 'transparent !important',
                  border: '1px solid white',
                  height: 25,
                  width: 25,
                  px: 1,
                  mr: 1,
                }}
                variant={'outlined'}
                disableRipple
                aria-label="premium-job"
              />
            )}
            {isDeviceLarge ? (
              tags.map(
                (tag, index) =>
                  tag?.length > 0 && <Pill key={index} text={tag} />
              )
            ) : (
              <>
                {tags
                  .slice(0, tagsSize)
                  .map(
                    (tag, index) =>
                      tag?.length > 0 && <Pill key={index} text={tag} />
                  )}

                {tagsSize === 2 && tags.length > 2 && (
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: 'white',
                      borderRadius: '16px',
                      mr: isDeviceLarge ? 1 : 0,
                      p: '4px 0',
                      textDecoration: 'none',
                      fontSize: 12,
                      fontWeight: 400,
                      color: 'text.primary',
                      lineHeight: '16px',
                      display: 'inline-block',
                      mb: isDeviceLarge ? 1 : 0,
                    }}
                    onClick={() => setTagsSize(tags.length)}
                  >
                    +{tags.length - 2}
                  </Button>
                )}
              </>
            )}
          </Box>
          <Typography
            color="white"
            fontSize={16}
            fontWeight={200}
            data-testid="job-posted-on"
          >
            {props.brandedJob ? (
              isLargeDevice ? (
                <></>
              ) : (
                <Typography
                  sx={{
                    fontSize: {xxs: 12, md: 14},
                    color: '#FFF',
                    fontWeight: 200,
                  }}
                >
                  Posted {dateToDuration(moment(createdTime).toDate(), true)}
                </Typography>
              )
            ) : (
              <>
                Posted on: <Typography component="span">{postedOn}</Typography>
              </>
            )}
          </Typography>
          {!isDeviceLarge && hasShowcase && !props.brandedJob && (
            <Box className="showcase-wrapper" display="flex" mt={2}>
              <Box className="showcase-images">
                {showcaseImages?.slice(2, 5).map((image, idx) => (
                  <Image
                    key={idx}
                    alt="showcase-image"
                    width={40}
                    height={40}
                    src={image}
                    style={{
                      marginRight: 10,
                      borderRadius: '50%',
                    }}
                  />
                ))}
              </Box>
              <IconButton
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: 'white',
                  transform: 'rotate(180deg)',
                }}
                onClick={() => openUrl(showcaseUrl, '_blank')}
              >
                <Image src={LeftArrow} alt="showcase" width={20} height={20} />
              </IconButton>
            </Box>
          )}
        </Box>
        {isDeviceLarge && hasShowcase && !props.brandedJob && (
          <Box
            className="showcase-wrapper"
            display="flex"
            flexDirection={isDeviceLarge ? 'column' : 'row'}
            position="absolute"
            right="calc((100vw - 1440px) / 2 + 64px)"
          >
            <Box className="showcase-images" mb={2}>
              {showcaseImages?.slice(2, 5).map((image, idx) => (
                <Image
                  key={idx}
                  alt="showcase-image"
                  width={isDeviceLarge ? 68 : 40}
                  height={isDeviceLarge ? 68 : 40}
                  src={image}
                  style={{
                    marginRight: 10,
                    borderRadius: isDeviceLarge ? 10 : '50%',
                  }}
                />
              ))}
            </Box>
            <Button
              variant="contained"
              sx={{
                fontSize: 12,
                color: 'black',
                bgcolor: 'white !important',
                maxWidth: 218,
                px: 0,
              }}
              onClick={() => {
                TrackEventUBA('companyBannerClick', 'click', {
                  page: getUbaPageName(),
                  jobID: Number(jobId) || '',
                  companyID: showcaseCompanyId || '',
                  companyName: showcaseName || '',
                  sessionUsername: sessionStorage.getItem('userId') || '',
                });

                openUrl(showcaseUrlWithQueryParams, '_blank');
              }}
            >
              CLICK HERE TO VIEW SHOWCASE
            </Button>
          </Box>
        )}
        {isLargeDevice && props.brandedJob && (
          <Box
            display="flex"
            alignItems="end"
            position="absolute"
            right="calc((100vw - 1440px) / 2 + 48px)"
            sx={{
              '@media (max-width: 1440px)': {
                right: '48px',
              },
            }}
            flexDirection={'column'}
          >
            <Tooltip
              variant={VersionVariants.VERSION_HIRIST}
              arrow
              id="premium_job"
              className="set-tooltip"
              title={
                <Box
                  sx={{
                    bgcolor: '#fbe3e6',
                    color: 'red',
                  }}
                >
                  premium job
                </Box>
              }
              sx={{
                '& .MuiTooltip-tooltip, & .MuiTooltip-arrow:before': {
                  bgcolor: '#fbe3e6',
                },
              }}
            >
              <Box display="inline" p={0}>
                <Button
                  shape="circular"
                  icon={<FaBookmark />}
                  size="small"
                  color="secondary"
                  sx={{
                    color: '#b4b9d8',
                    border: 'none',
                    bgcolor: 'white !important',
                    height: props.brandedJob ? 24 : 32,
                    width: props.brandedJob ? 24 : 32,
                    px: props.brandedJob ? 1 : 1.45,
                    py: props.brandedJob ? 1 : 1.5,
                    cursor: 'default',
                    mt: 0.5,
                  }}
                  variant={'outlined'}
                  disableRipple
                  aria-label="premium-job"
                />
              </Box>
            </Tooltip>
            <Typography
              sx={{fontSize: 14, color: '#FFF', fontWeight: 400, mt: 1}}
            >
              Posted {dateToDuration(moment(createdTime).toDate(), true)}
            </Typography>
          </Box>
        )}
      </HiristBanner>
    </Box>
  );
};

export default Header;
